import { useState, useCallback, useEffect } from 'react';
import type { JournalEntry } from '../types';

interface MomentAdvice {
  entryId: string;
  oracleInsight: string;
  shortTermAction: string;
  shortTermTimeframe: string;
  shortTermCompleted?: boolean;
  mediumTermGoal: string;
  mediumTermTimeframe: string;
  mediumTermCompleted?: boolean;
  longTermVision: string;
  longTermTimeframe: string;
  longTermCompleted?: boolean;
  emotionGradient: string;
  inspirationalQuote: string;
  connectedValues: string[];
  connectedGoals: string[];
}

interface WeeklyRecap {
  id: number;
  weekStart: Date;
  weekEnd: Date;
  oracleMessage: string;
  keyInsight: string;
  dominantEmotion?: string;
  topPattern?: string;
  recommendedAction?: string;
  weeklyScore?: number;
  entriesCount: number;
  isViewed: boolean;
}

export function useMomentAdvice() {
  const [adviceMap, setAdviceMap] = useState<Map<string, MomentAdvice>>(new Map());
  const [weeklyRecaps, setWeeklyRecaps] = useState<WeeklyRecap[]>([]);
  const [generatingAdviceForIds, setGeneratingAdviceForIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  const fetchAllAdvice = useCallback(async () => {
    try {
      const response = await fetch('/api/moment-advice', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const map = new Map<string, MomentAdvice>();
        data.forEach((advice: any) => {
          map.set(advice.entryId, {
            entryId: advice.entryId,
            oracleInsight: advice.oracleInsight,
            shortTermAction: advice.shortTermAction,
            shortTermTimeframe: advice.shortTermTimeframe,
            shortTermCompleted: advice.shortTermCompleted,
            mediumTermGoal: advice.mediumTermGoal,
            mediumTermTimeframe: advice.mediumTermTimeframe,
            mediumTermCompleted: advice.mediumTermCompleted,
            longTermVision: advice.longTermVision,
            longTermTimeframe: advice.longTermTimeframe,
            longTermCompleted: advice.longTermCompleted,
            emotionGradient: advice.emotionGradient,
            inspirationalQuote: advice.inspirationalQuote,
            connectedValues: advice.connectedValues || [],
            connectedGoals: advice.connectedGoals || [],
          });
        });
        setAdviceMap(map);
      }
    } catch (error) {
      console.error('Error fetching moment advice:', error);
    }
  }, []);

  const fetchWeeklyRecaps = useCallback(async () => {
    try {
      const response = await fetch('/api/weekly-recap', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setWeeklyRecaps(data.map((recap: any) => ({
          id: recap.id,
          weekStart: new Date(recap.weekStart),
          weekEnd: new Date(recap.weekEnd),
          oracleMessage: recap.oracleMessage,
          keyInsight: recap.keyInsight,
          dominantEmotion: recap.dominantEmotion,
          topPattern: recap.topPattern,
          recommendedAction: recap.recommendedAction,
          weeklyScore: recap.weeklyScore,
          entriesCount: recap.entriesCount,
          isViewed: recap.isViewed,
        })));
      }
    } catch (error) {
      console.error('Error fetching weekly recaps:', error);
    }
  }, []);

  const generateAdvice = useCallback(async (entry: JournalEntry) => {
    const entryId = entry.id;
    
    setGeneratingAdviceForIds(prev => new Set(prev).add(entryId));
    
    try {
      const response = await fetch(`/api/moment-advice/${entryId}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          content: entry.content,
          emotion: entry.emotion,
          category: entry.category,
          aiInsight: entry.aiInsight,
        }),
      });
      
      if (response.ok) {
        const newAdvice = await response.json();
        setAdviceMap(prev => {
          const updated = new Map(prev);
          updated.set(entryId, {
            entryId: newAdvice.entryId,
            oracleInsight: newAdvice.oracleInsight,
            shortTermAction: newAdvice.shortTermAction,
            shortTermTimeframe: newAdvice.shortTermTimeframe,
            shortTermCompleted: newAdvice.shortTermCompleted,
            mediumTermGoal: newAdvice.mediumTermGoal,
            mediumTermTimeframe: newAdvice.mediumTermTimeframe,
            mediumTermCompleted: newAdvice.mediumTermCompleted,
            longTermVision: newAdvice.longTermVision,
            longTermTimeframe: newAdvice.longTermTimeframe,
            longTermCompleted: newAdvice.longTermCompleted,
            emotionGradient: newAdvice.emotionGradient,
            inspirationalQuote: newAdvice.inspirationalQuote,
            connectedValues: newAdvice.connectedValues || [],
            connectedGoals: newAdvice.connectedGoals || [],
          });
          return updated;
        });
      }
    } catch (error) {
      console.error('Error generating advice:', error);
    } finally {
      setGeneratingAdviceForIds(prev => {
        const updated = new Set(prev);
        updated.delete(entryId);
        return updated;
      });
    }
  }, []);

  const completeAction = useCallback(async (entryId: string, actionType: 'short' | 'medium' | 'long', completed: boolean) => {
    try {
      const response = await fetch(`/api/moment-advice/${entryId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ actionType, completed }),
      });

      if (response.ok) {
        const updated = await response.json();
        setAdviceMap(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(entryId);
          if (existing) {
            newMap.set(entryId, {
              ...existing,
              shortTermCompleted: updated.shortTermCompleted,
              mediumTermCompleted: updated.mediumTermCompleted,
              longTermCompleted: updated.longTermCompleted,
            });
          }
          return newMap;
        });
      }
    } catch (error) {
      console.error('Error toggling action completion:', error);
    }
  }, []);

  const markRecapViewed = useCallback(async (id: number) => {
    try {
      await fetch(`/api/weekly-recap/${id}/viewed`, {
        method: 'POST',
        credentials: 'include',
      });
      
      setWeeklyRecaps(prev =>
        prev.map(recap =>
          recap.id === id ? { ...recap, isViewed: true } : recap
        )
      );
    } catch (error) {
      console.error('Error marking recap viewed:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchAllAdvice(), fetchWeeklyRecaps()]);
      setIsLoading(false);
    };
    loadData();
  }, [fetchAllAdvice, fetchWeeklyRecaps]);

  return {
    adviceMap,
    weeklyRecaps,
    generatingAdviceForIds,
    isLoading,
    generateAdvice,
    completeAction,
    markRecapViewed,
    refetch: () => Promise.all([fetchAllAdvice(), fetchWeeklyRecaps()]),
  };
}
